// ─── auth-callback-classify.js ───────────────────────────────────────────────
// React-free classic script (node-unit-testable, same pattern as
// rich-text-sanitize.js). It owns what a sign-in that did NOT finish means, and
// the sentence the person reads, for BOTH apps that sign in: apps/fe reads it off
// `window`, apps/console imports it (see the paired auth-callback-classify.d.ts).
//
// ── History, kept short ──────────────────────────────────────────────────────
// Until 2026-10-16 this file consumed Auth0's RETURN LEG in the browser
// (`?code=…&state=…`), in the one order that could not strand anyone, after Omar
// opened the staging console on a stale link (2026-08-24) and got "Invalid state"
// with no Sign in button. Since plan 27 (approved by Omar on 2026-10-16) OUR
// SERVER receives that return and keeps the renewal card in a protected cookie;
// what reaches the page is the server's own short report — `stale`, `refused` or
// `fault` — in the sign-in window's message, or in the address of a full-page
// sign-in. What this file still owns is unchanged in spirit:
//
//   · ONE set of sentences, the ones Omar approved with the sign-in prototypes on
//     2026-10-15, so the two apps cannot drift into two wordings;
//   · a failure is RENDERED, never thrown at a boot handler and never swallowed:
//     every function here returns a notice and none of them throws.
(function () {
  "use strict";

  var STALE_MESSAGE =
    "That sign-in link has expired or was opened in another browser. Sign in again.";

  // No "Try again" here, unlike the popup's own failures: this is the case where
  // retrying is not the remedy, and the technical text that follows says why.
  var FAULT_MESSAGE = "Sign-in didn\u2019t finish.";

  /**
   * What a sign-in that did NOT finish means, now that OUR server runs it (plan 27,
   * Omar 2026-10-16). The server's callback reports one of three reasons, in this
   * file's own vocabulary, so the sentences stay the ones already approved:
   *   · stale   — no sign-in was started in this browser, or another one's came back
   *               (the server also files a state mismatch here, as `classify…` does);
   *   · refused — Auth0 itself said no: its own words are the message;
   *   · fault   — anything else, with the technical text as the detail.
   *
   * @param {{reason?: string|null, error?: string|null, description?: string|null}} result
   * @returns {{kind: string, recoverable: boolean, message: string, detail: string|null}}
   */
  function noticeForSignInFailure(result) {
    var r = result || {};
    if (r.reason === "stale") {
      return { kind: "stale", recoverable: true, message: STALE_MESSAGE, detail: null };
    }
    if (r.reason === "refused") {
      return {
        kind: "refused",
        recoverable: true,
        message: r.description || "Sign-in was refused. Try again, or contact Dynamo if it keeps happening.",
        detail: r.error || null,
      };
    }
    var technical = [r.error, r.description].filter(function (x) { return !!x; }).join(": ");
    return { kind: "fault", recoverable: false, message: FAULT_MESSAGE, detail: technical || null };
  }

  /**
   * A FULL-PAGE sign-in that came back from our server without finishing:
   * `?signin=failed&reason=…&error=…&error_description=…`. Reads it once, takes those
   * four out of the address bar (so a reload does not say it again), and returns the
   * notice to RENDER; null when the address is not one.
   *
   * @param {{href: string, history: {replaceState: Function}}} options
   */
  function consumeSignInReturn(options) {
    var opts = options || {};
    var url;
    try { url = new URL(opts.href); } catch (e) { return null; }
    if (url.searchParams.get("signin") !== "failed") return null;
    var notice = noticeForSignInFailure({
      reason: url.searchParams.get("reason"),
      error: url.searchParams.get("error"),
      description: url.searchParams.get("error_description"),
    });
    ["signin", "reason", "error", "error_description"].forEach(function (k) { url.searchParams.delete(k); });
    if (opts.history && typeof opts.history.replaceState === "function") {
      opts.history.replaceState({}, "", url.pathname + url.search + url.hash);
    }
    return notice;
  }

  var api = {
    noticeForSignInFailure: noticeForSignInFailure,
    consumeSignInReturn: consumeSignInReturn,
    AUTH_CALLBACK_STALE_MESSAGE: STALE_MESSAGE,
    AUTH_CALLBACK_FAULT_MESSAGE: FAULT_MESSAGE,
  };

  if (typeof window !== "undefined") {
    window.noticeForSignInFailure = api.noticeForSignInFailure;
    window.consumeSignInReturn = api.consumeSignInReturn;
    window.AUTH_CALLBACK_STALE_MESSAGE = api.AUTH_CALLBACK_STALE_MESSAGE;
    window.AUTH_CALLBACK_FAULT_MESSAGE = api.AUTH_CALLBACK_FAULT_MESSAGE;
  }
  if (typeof module !== "undefined" && module.exports) module.exports = api;
})();
